import { useState } from 'react';
import SteelLinkLogo from '@/components/feature/SteelLinkLogo';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('loading');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d77qusqdqn9mib2tqcfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubStatus(res.ok ? 'done' : 'error');
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <footer style={{ backgroundColor: '#0a1929' }} className="text-gray-400">

      {/* Newsletter + CTA strip */}
      <div className="border-b border-white/10" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[1420px] mx-auto px-6 py-10 flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Left: Get Quote CTA */}
          <div className="text-center lg:text-left">
            <h3 className="text-white font-black text-xl mb-1">Ready to place an order?</h3>
            <p className="text-white/50 text-sm">Get your free FOB quote in under 2 hours.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="/quote"
              className="px-6 py-3 bg-white font-black text-sm rounded-lg cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap text-center"
              style={{ color: '#0a2342' }}
            >
              <i className="ri-file-list-3-line mr-2" />Get a Free Quote
            </a>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-black text-sm rounded-lg cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" />WhatsApp Us
            </a>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-12 bg-white/10 flex-shrink-0" />

          {/* Right: Newsletter */}
          <div className="w-full lg:w-auto lg:min-w-[320px]">
            <p className="text-white font-bold text-sm mb-1 text-center lg:text-left">
              <i className="ri-mail-send-line mr-1.5" style={{ color: '#7eb8f7' }} />
              Get steel price updates
            </p>
            <p className="text-white/40 text-xs mb-3 text-center lg:text-left">Weekly market prices, no spam.</p>
            {subStatus === 'done' ? (
              <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                <i className="ri-checkbox-circle-fill" />Subscribed! We&apos;ll be in touch.
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-white/10 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-white/50 min-w-0"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'loading'}
                  className="px-4 py-2.5 text-white text-sm font-black rounded-lg cursor-pointer transition-all whitespace-nowrap flex-shrink-0"
                  style={{ backgroundColor: '#1e5fa8' }}
                >
                  {subStatus === 'loading' ? <i className="ri-loader-4-line animate-spin" /> : 'Subscribe'}
                </button>
              </form>
            )}
            {subStatus === 'error' && <p className="text-red-400 text-xs mt-1">Something went wrong. Try again.</p>}
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="max-w-[1420px] mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand + Contact */}
        <div className="text-center sm:text-left">
          <div className="mb-4 flex justify-center sm:justify-start">
            <SteelLinkLogo variant="light" size="lg" />
          </div>
          <p className="text-sm leading-relaxed text-gray-500 mb-5">
            China-based steel supplier. We connect buyers worldwide with trusted Chinese mills — delivering quality steel, on time, every time.
          </p>

          {/* Social links */}
          <div className="flex gap-3 justify-center sm:justify-start mb-5">
            {[
              { icon: 'ri-linkedin-box-fill', href: 'https://linkedin.com', label: 'LinkedIn' },
              { icon: 'ri-whatsapp-fill', href: 'https://wa.me/8617671262710', label: 'WhatsApp' },
              { icon: 'ri-facebook-box-fill', href: 'https://facebook.com', label: 'Facebook' },
              { icon: 'ri-youtube-fill', href: 'https://youtube.com', label: 'YouTube' },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer"
                style={{ backgroundColor: 'rgba(126,184,247,0.12)', color: '#7eb8f7' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(126,184,247,0.12)'; }}
              >
                <i className={`${s.icon} text-base`} />
              </a>
            ))}
          </div>

          <ul className="text-sm space-y-2 text-gray-400">
            <li className="flex items-center justify-center sm:justify-start gap-2">
              <i className="ri-phone-line flex-shrink-0" style={{ color: '#7eb8f7' }} />
              <a href="tel:+8617671262710" className="hover:text-white">+86 17671262710</a>
            </li>
            <li className="flex items-center justify-center sm:justify-start gap-2">
              <i className="ri-whatsapp-line flex-shrink-0" style={{ color: '#7eb8f7' }} />
              <a href="https://wa.me/8617671262710" target="_blank" rel="noopener noreferrer" className="hover:text-white">+86 17671262710</a>
            </li>
            <li className="flex items-center justify-center sm:justify-start gap-2">
              <i className="ri-mail-line flex-shrink-0" style={{ color: '#7eb8f7' }} />
              <a href="mailto:support@steelink.com" className="hover:text-white">support@steelink.com</a>
            </li>
            <li className="flex gap-2 justify-center sm:justify-start">
              <i className="ri-map-pin-line mt-0.5 flex-shrink-0" style={{ color: '#7eb8f7' }} />
              <span>Room 906, Zhanghe Shengde Building, Huangpu Avenue, Tianhe District, Guangzhou City, Guangdong Province, China</span>
            </li>
          </ul>
        </div>

        {/* Construction & Building */}
        <div className="text-center sm:text-left">
          <h3 className="text-white font-bold text-sm mb-4 flex items-center justify-center sm:justify-start gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#7eb8f7' }} />
            Construction &amp; Building
          </h3>
          <ul className="space-y-2 text-sm">
            {[
              { label: 'Cold-Rolled Coil (CRC)', href: '/products/item/cold-rolled-coil' },
              { label: 'Hot-Rolled Coil (HRC)', href: '/products/item/hot-rolled-coil' },
              { label: 'Pre-Painted Galvanized Coil', href: '/products/item/pre-painted-galvanized-coil' },
              { label: 'Rebar HRB400', href: '/products/item/rebar-hrb400' },
              { label: 'H-Beam Steel', href: '/products/item/h-beam-steel' },
              { label: 'Wire Rod', href: '/products/item/wire-rod' },
              { label: 'Square Tube', href: '/products/item/square-tube' },
            ].map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="hover:text-white transition-colors"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#7eb8f7'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ''; }}
                >{item.label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Piping & Roofing */}
        <div className="text-center sm:text-left">
          <h3 className="text-white font-bold text-sm mb-4 flex items-center justify-center sm:justify-start gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#7eb8f7' }} />
            Plumbing &amp; Piping
          </h3>
          <ul className="space-y-2 text-sm mb-6">
            {[
              { label: 'ERW Black Pipe', href: '/products/item/erw-black-pipe' },
              { label: 'ERW Galvanized Pipe', href: '/products/item/erw-galvanized-pipe' },
              { label: 'Seamless Pipe', href: '/products/item/seamless-pipe' },
              { label: 'LSAW Pipe', href: '/products/item/lsaw-pipe' },
              { label: 'Hot-Dip Galv Pipe', href: '/products/item/hot-dip-galvanized-pipe-threaded' },
            ].map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="transition-colors"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#7eb8f7'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ''; }}
                >{item.label}</a>
              </li>
            ))}
          </ul>
          <h3 className="text-white font-bold text-sm mb-4 flex items-center justify-center sm:justify-start gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#7eb8f7' }} />
            Roofing &amp; Cladding
          </h3>
          <ul className="space-y-2 text-sm">
            {[
              { label: 'Corrugated Roofing Sheet', href: '/products/item/corrugated-roofing-sheet' },
              { label: 'PPGI Sheet', href: '/products/item/ppgi-sheet' },
              { label: 'Galvalume Steel Coil', href: '/products/item/galvalume-steel-coil' },
              { label: 'Purlin Gi 2.0', href: '/products/item/purlin-gi' },
            ].map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="transition-colors"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#7eb8f7'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ''; }}
                >{item.label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Top Categories + Quick Links + Certs */}
        <div className="text-center sm:text-left">
          <h3 className="text-white font-bold text-sm mb-4 flex items-center justify-center sm:justify-start gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#7eb8f7' }} />
            Top Categories
          </h3>
          <ul className="space-y-2 text-sm mb-6">
            {[
              { label: 'Construction & Building', href: '/products/construction-building' },
              { label: 'Roofing & Cladding', href: '/products/roofing-cladding' },
              { label: 'Plumbing & Piping', href: '/products/plumbing-piping' },
              { label: 'Industrial & Structural Steel', href: '/products/industrial-structural' },
              { label: 'Wire & Mesh', href: '/products/wire-mesh' },
            ].map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="transition-colors flex items-center justify-center sm:justify-start gap-1.5"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#7eb8f7'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ''; }}
                >
                  <i className="ri-arrow-right-s-line" style={{ color: '#7eb8f7' }} />{item.label}
                </a>
              </li>
            ))}
          </ul>

          <h3 className="text-white font-bold text-sm mb-3">Quick Links</h3>
          <ul className="space-y-2 text-sm mb-6">
            {[
              { label: 'About Us', href: '/about' },
              { label: 'Get a Quote', href: '/quote' },
              { label: 'Sourcing Case Study', href: '/sourcing-case' },
              { label: 'Shipping Ports', href: '/#shipping' },
              { label: 'FAQ', href: '/faq' },
              { label: 'Contact Us', href: '/contact' },
            ].map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="transition-colors flex items-center justify-center sm:justify-start gap-1.5"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#7eb8f7'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ''; }}
                >
                  <i className="ri-arrow-right-s-line" style={{ color: '#7eb8f7' }} />{item.label}
                </a>
              </li>
            ))}
          </ul>

          <h3 className="text-white font-bold text-sm mb-3">Certifications</h3>
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {['ISO-9001', 'SGS', 'CE', 'TUV'].map((cert) => (
              <span key={cert} className="px-2.5 py-1 text-white text-xs font-black rounded" style={{ backgroundColor: '#0a2342' }}>{cert}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1420px] mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-600 text-center">
          <span>Copyright &copy; 2026 Steel Link Global | All Rights Reserved.</span>
          <div className="flex gap-4">
            <a href="/legal" className="transition-colors hover:text-white">Privacy Policy</a>
            <a href="/legal#terms" className="transition-colors hover:text-white">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
